import React from 'react';
import { Avatar, Box, Card, CardContent, Container, Grid, Stack, Typography } from '@mui/material';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import {
  accentCardSx,
  eyebrowSx,
  heroPanelSx,
  iconBadgeSx,
  pageHeadlineSx,
  subtleCopySx,
  warmPanelSx,
} from './designSystem';
import jonathanImage from './images/jonathan.jpeg';

const profileCards = [
  {
    icon: <SchoolOutlinedIcon />,
    accent: '#13947F',
    tint: 'rgba(19,148,127,0.12)',
    title: 'Student builder',
    body: 'I am a computer science major and a junior at UCI, building products while still living the reality of how students actually use the web every day.',
  },
  {
    icon: <WorkspacePremiumOutlinedIcon />,
    accent: '#46729B',
    tint: 'rgba(70,114,155,0.12)',
    title: 'Proof of execution',
    body: 'Before BlockWebAI, I created Tweet Flash, a Twitter webscraper product that generated more than $60k, which gave me real experience shipping, selling, and improving software people paid for.',
  },
  {
    icon: <SmartDisplayOutlinedIcon />,
    accent: '#C08A2D',
    tint: 'rgba(192,138,45,0.12)',
    title: 'Creator',
    body: 'I also run a YouTube channel with 1.19k subscribers, which is part of the broader creative and entrepreneurial work I have been building alongside software.',
  },
];

function AboutUs() {
  return (
    <Container sx={{ py: { xs: 6, md: 8 } }}>
      <Stack spacing={5}>
        <Box sx={{ ...heroPanelSx, p: { xs: 3.5, md: 6 } }}>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={eyebrowSx}>
                About Jonathan
              </Typography>
              <Typography variant="h2" sx={{ ...pageHeadlineSx, mt: 1.5, mb: 2.5 }}>
                Built from a student's view of how the web actually pulls attention apart.
              </Typography>
              <Typography sx={{ ...subtleCopySx, lineHeight: 1.85, maxWidth: 680, fontSize: '1.02rem' }}>
                I&apos;m Jonathan Larson, a computer science major at UCI. I built BlockWebAI because I know firsthand how quickly the internet can pull attention away from what matters. The goal was to create something more precise than blunt category filters: a system that helps schools keep useful content accessible while still catching the pages and distractions that derail focus.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: '22px',
                  bgcolor: 'rgba(255,255,255,0.8)',
                  border: '1px solid rgba(11,46,67,0.08)',
                  boxShadow: '0 2px 4px rgba(11,46,67,0.04), 0 24px 56px -24px rgba(11,46,67,0.2)',
                }}
              >
                <Stack spacing={2.25}>
                  <Avatar
                    src={jonathanImage}
                    alt="Jonathan Larson"
                    variant="rounded"
                    sx={{
                      width: '100%',
                      height: { xs: 280, sm: 360, md: 380 },
                      borderRadius: '16px',
                      border: '1px solid rgba(11,46,67,0.08)',
                    }}
                  />
                  <Box>
                    <Typography variant="h5" sx={{ color: '#0B2E43', mb: 0.5 }}>
                      Jonathan Larson
                    </Typography>
                    <Typography sx={{ ...subtleCopySx, fontSize: '0.94rem' }}>
                      Founder of BlockWebAI, creator of Tweet Flash, and a UCI computer science student building education-focused software. Connect with me on{' '}
                      <Box
                        component="a"
                        href="https://www.linkedin.com/in/jonathanentreprenur/"
                        target="_blank"
                        rel="noreferrer"
                        sx={{ color: '#0E4D6E', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        LinkedIn
                      </Box>
                      .
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={3}>
          {profileCards.map((card) => (
            <Grid item xs={12} md={4} key={card.title}>
              <Card sx={{ ...accentCardSx(card.accent), height: '100%' }}>
                <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                  <Box sx={{ ...iconBadgeSx(card.tint, card.accent), mb: 2 }}>
                    {card.icon}
                  </Box>
                  <Typography variant="h5" sx={{ color: '#0B2E43', mb: 1.25 }}>
                    {card.title}
                  </Typography>
                  <Typography sx={subtleCopySx}>{card.body}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={warmPanelSx}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Box sx={iconBadgeSx('rgba(19,148,127,0.14)', '#13947F')}>
                <PsychologyOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                  Why I built it
                </Typography>
                <Typography sx={{ ...subtleCopySx, lineHeight: 1.8 }}>
                  The core idea is simple: schools should not have to choose between keeping useful material accessible and protecting focus, safety, and judgment. That tension is what pushed me to build a more precise filtering approach in the first place.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}

export default AboutUs;
